import {
  Box,
  CircularProgress,
  Grid,
  Typography,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import { useState } from "react";
import { useSelector } from "react-redux";
import type { AdminProfileData } from "screens/Admin/types";
import {
  selectEmail,
  selectFirstName,
  selectLastName,
} from "../../../../redux/authSlice";
import adminProfileSectionStyles from "./AdminProfileSection.styles";

const AdminProfileSection = () => {
  const theme = useTheme();
  const styles = adminProfileSectionStyles;
  const firstName = useSelector(selectFirstName);
  const lastName = useSelector(selectLastName);
  const email = useSelector(selectEmail);
  const [profile, setProfile] = useState<AdminProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // const load = useCallback(async () => {
  //   setLoading(true);
  //   setError(null);
  //   try {
  //     const data = await getAdminProfile();
  //     setProfile(data);
  //   } catch (e: any) {
  //     setError(null);
  //     setProfile({
  //       adminId: "",
  //       firstName: firstName ?? "",
  //       lastName: lastName ?? "",
  //       email: email ?? "",
  //       starredBlogsCount: 12,
  //       draftBlogsCount: 5,
  //       publishedBlogsCount: 16,
  //       activitySummary: {
  //         lastLogin: new Date().toISOString().slice(0, 10),
  //         blogsCreatedThisMonth: 4,
  //         blogsPublishedThisMonth: 3,
  //       },
  //     });
  //   } finally {
  //     setLoading(false);
  //   }
  // }, [firstName, lastName, email]);

  // useEffect(() => {
  //   load();
  // }, [load]);

  if (loading && !profile) {
    return (
      <Box id="profile" sx={styles.section}>
        <Box sx={{ py: 8, textAlign: "center" }}>
          <CircularProgress size={48} sx={{ color: "#f8481c" }} />
          <Typography sx={{ mt: 2 }}>Loading profile...</Typography>
        </Box>
      </Box>
    );
  }

  const p = profile ?? {
    adminId: "",
    firstName: firstName ?? "",
    lastName: lastName ?? "",
    email: email ?? "",
    starredBlogsCount: 0,
    draftBlogsCount: 0,
    publishedBlogsCount: 0,
    activitySummary: {},
  };

  return (
    <Box id="profile" sx={styles.section}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
        Admin Profile
      </Typography>
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Box sx={styles.card}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Basic details
              </Typography>
              <Typography variant="h6" fontWeight={600}>
                {p.firstName} {p.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {p.email}
              </Typography>
            </Box>
          </motion.div>
        </Grid>
        <Grid item xs={12} sm={4} md={2}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <Box sx={styles.card}>
              <Typography variant="body2" color="text.secondary">
                Starred blogs
              </Typography>
              <Typography sx={styles.stat}>{p.starredBlogsCount}</Typography>
            </Box>
          </motion.div>
        </Grid>
        <Grid item xs={12} sm={4} md={2}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.15 }}
          >
            <Box sx={styles.card}>
              <Typography variant="body2" color="text.secondary">
                Draft blogs
              </Typography>
              <Typography sx={styles.stat}>{p.draftBlogsCount}</Typography>
            </Box>
          </motion.div>
        </Grid>
        <Grid item xs={12} sm={4} md={2}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <Box sx={styles.card}>
              <Typography variant="body2" color="text.secondary">
                Published
              </Typography>
              <Typography sx={styles.stat}>{p.publishedBlogsCount}</Typography>
            </Box>
          </motion.div>
        </Grid>
        {p.activitySummary && (
          <Grid item xs={12}>
            <Box sx={styles.card}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Activity summary
              </Typography>
              <Typography variant="body2">
                Last login:{" "}
                {"lastLogin" in p.activitySummary
                  ? (p.activitySummary.lastLogin ?? "—")
                  : "—"}
              </Typography>
              <Typography variant="body2">
                Blogs created this month:{" "}
                {"blogsCreatedThisMonth" in p.activitySummary
                  ? (p.activitySummary.blogsCreatedThisMonth ?? 0)
                  : 0}
              </Typography>
              <Typography variant="body2">
                Blogs published this month:{" "}
                {"blogsPublishedThisMonth" in p.activitySummary
                  ? (p.activitySummary.blogsPublishedThisMonth ?? 0)
                  : 0}
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default AdminProfileSection;
